import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import BiotechIcon from '@mui/icons-material/Biotech';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Box,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { navDrawerScrollSx } from '../layout/drawerConstants';
import type { NavigationMenuItem } from '../api/strapiNavigationMenu';
import { APP_NAME_FULL, APP_NAME_SHORT } from '../appMeta';
import { useAuth } from '../auth/AuthContext';
import { useNavigationMenuQuery } from '../query/hooks';

const navItemActiveBg = 'rgba(255, 255, 255, 0.12)';
const navItemHoverBg = 'rgba(255, 255, 255, 0.06)';
const navDivider = 'rgba(255, 255, 255, 0.12)';

const navChromeSx = {
  flex: 1,
  minHeight: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  color: 'rgba(255, 255, 255, 0.92)',
  '& .MuiListItemIcon-root': {
    color: 'rgba(255, 255, 255, 0.78)',
  },
  '& .MuiListItemButton-root:hover': {
    bgcolor: navItemHoverBg,
  },
  '& .MuiSvgIcon-root': {
    color: 'inherit',
  },
} as const;

/** Access codes that must not appear in the left drawer (RBAC-only). */
const HIDDEN_NAV_ACCESS_CODES = new Set([
  'can_view_pre_survey',
  'can_view_sample_information',
  'can_view_survey_schedule',
]);

/** When `nav_path` is empty on an access, still link known codes to app routes. */
const FALLBACK_HREF: Record<string, string> = {
  can_view_dashboard: '/dashboard',
  can_view_customer: '/customers',
  can_view_quotation: '/quotations',
  can_view_marketing: '/dashboard',
  can_view_work_order: '/sampling/work-orders',
  can_view_sampling_schedule: '/sampling/schedules',
  can_view_survey: '/sampling/sampling',
  can_view_sample_preservation: '/sampling/sample-preservation',
  can_view_laboratory_result: '/sampling/laboratory-results',
  can_view_hasil_uji_letter: '/sampling/hasil-uji-letter',
  can_view_invoice: '/keuangan/invoices',
  can_view_test_result_master: '/masters/test-results',
  can_view_lab_grouping: '/masters/groupings',
  can_view_lab_jenis_pengukuran: '/masters/jenis-pengukurans',
  can_view_lab_baku_mutu: '/masters/baku-mutus',
  can_view_lab_referensi: '/masters',
  can_view_lab_parameter_uji: '/masters/parameter-ujis',
  can_view_lab_parameter_uji_metoda: '/masters/parameter-uji-metodas',
  can_view_lab_sni_method: '/master/sni-methods',
  can_view_lab_tool: '/master/tools',
  can_view_lab_freelance_user: '/masters/freelance-users',
  can_view_lab_baku_mutu_parameter_uji: '/masters/baku-mutu-parameter-ujis',
  can_view_lab_jenis_pengukuran_perlengkapan: '/masters/jenis-pengukuran-perlengkapans',
  can_view_lab_peralatan_to_parameter_uji: '/masters/peralatan-to-parameter-ujis',
  can_view_lab_form_question: '/masters/form-questions',
  can_view_lab_form_question_option: '/masters/form-question-options',
  can_view_lab_parameter_uji_analis: '/masters/parameter-uji-analises',
  can_view_lab_analis_type_data: '/masters/analis-type-data',
  can_view_permission: '/superadmin/permissions',
  can_view_user: '/superadmin/users',
  can_view_user_type: '/superadmin/user-types',
  can_view_purchase_order: '/dashboard',
};

/** Presentable section title for permission `group_name` from the API. */
function formatNavGroupLabel(label: string): string {
  const map: Record<string, string> = {
    sampling: 'Sampling',
    crm: 'CRM',
    general: 'General',
    keuangan: 'Keuangan',
    superadmin: 'Superadmin',
    master_data: 'Master data',
  };
  const k = label.trim().toLowerCase();
  return map[k] ?? label;
}

/** Left drawer section order (API returns alphabetical `rest`; normalize here). */
const NAV_SECTION_ORDER = ['general', 'crm', 'sampling', 'keuangan', 'master_data', 'superadmin'] as const;

function compareNavDrawerGroups(a: { label: string }, b: { label: string }): number {
  const ak = a.label.trim().toLowerCase();
  const bk = b.label.trim().toLowerCase();
  const ia = (NAV_SECTION_ORDER as readonly string[]).indexOf(ak);
  const ib = (NAV_SECTION_ORDER as readonly string[]).indexOf(bk);
  const ra = ia === -1 ? NAV_SECTION_ORDER.length : ia;
  const rb = ib === -1 ? NAV_SECTION_ORDER.length : ib;
  if (ra !== rb) return ra - rb;
  return ak.localeCompare(bk);
}

function resolveItemHref(item: NavigationMenuItem): string | null {
  if (HIDDEN_NAV_ACCESS_CODES.has(item.access_code)) return null;
  const raw = item.href?.trim();
  if (raw) {
    const normalizedRaw = raw.startsWith('/') ? raw : `/${raw}`;
    // Keep legacy permission/nav_path values in sync with current route.
    if (normalizedRaw === '/sampling/survey') return '/sampling/sampling';
    return normalizedRaw;
  }
  const fb = FALLBACK_HREF[item.access_code];
  return fb ?? null;
}

function resolveItemLabel(item: NavigationMenuItem): string {
  if (item.access_code === 'can_view_survey') return 'Sampling';
  if (item.access_code === 'can_view_laboratory_result') return 'Hasil uji lab';
  if (item.access_code === 'can_view_hasil_uji_letter') return 'Hasil Uji Analis';
  if (item.access_code === 'can_view_invoice') return 'Invoice';
  return item.access_name;
}

type NavDrawerContentProps = {
  onNavigate?: () => void;
};

export function NavDrawerContent({ onNavigate }: NavDrawerContentProps) {
  const { jwt } = useAuth();
  const location = useLocation();
  const { data: menu, isPending, isError } = useNavigationMenuQuery(jwt);

  const pathIsSuperadmin = location.pathname.startsWith('/superadmin/');

  const [superOpen, setSuperOpen] = useState(() => pathIsSuperadmin);

  useEffect(() => {
    if (pathIsSuperadmin) setSuperOpen(true);
  }, [pathIsSuperadmin]);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const itemSx = (path: string) => ({
    bgcolor: isActive(path) ? navItemActiveBg : undefined,
    borderRadius: 1,
    mx: 0.5,
    '&.MuiListItemButton-root': { py: 1 },
  });

  const groups = menu?.groups ?? [];

  /** Groups that only contain superadmin links → render inside "Superadmin" collapse. */
  const { superGroups, mainGroups } = useMemo(() => {
    const superG: typeof groups = [];
    const mainG: typeof groups = [];
    for (const g of groups) {
      const onlySuper =
        g.items.length > 0 &&
        g.items.every((it) => {
          const h = resolveItemHref(it);
          return h?.startsWith('/superadmin');
        });
      if (onlySuper) superG.push(g);
      else mainG.push(g);
    }
    return { superGroups: superG, mainGroups: mainG };
  }, [groups]);

  const sortedMainGroups = useMemo(
    () => [...mainGroups].sort(compareNavDrawerGroups),
    [mainGroups],
  );
  const sortedSuperGroups = useMemo(
    () => [...superGroups].sort(compareNavDrawerGroups),
    [superGroups],
  );

  return (
    <Box sx={navChromeSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.5,
          px: 2,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: navDivider,
        }}
      >
        <BiotechIcon sx={{ color: 'primary.light' }} fontSize="medium" />
        <Typography variant="subtitle1" fontWeight={700} title={APP_NAME_FULL} sx={{ color: 'inherit' }}>
          {APP_NAME_SHORT}
        </Typography>
      </Box>
      <Box sx={navDrawerScrollSx}>
      <List component="nav" dense sx={{ px: 0.5, pt: 1, pb: 3 }} aria-label="Application sections">
        {isPending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} sx={{ color: 'primary.light' }} />
          </Box>
        )}
        {isError && (
          <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'error.light' }}>
            Menu unavailable. Check permission preset and API.
          </Typography>
        )}
        {!isPending &&
          groups.length === 0 &&
          !isError && (
            <Typography variant="body2" sx={{ px: 2, py: 1, color: 'rgba(255,255,255,0.7)' }}>
              No menu items. Ask an admin to assign a permission preset with navigable accesses.
            </Typography>
          )}

        {!isPending &&
          sortedMainGroups.map((g) => (
            <Box key={g.key} sx={{ mb: 0.5 }}>
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  px: 2,
                  pt: 1,
                  pb: 0.5,
                  color: 'rgba(255, 255, 255, 0.55)',
                  letterSpacing: 0.5,
                }}
              >
                {formatNavGroupLabel(g.label)}
              </Typography>
              {g.items.map((item) => {
                const to = resolveItemHref(item);
                if (!to) {
                  return null;
                }
                return (
                  <ListItemButton
                    key={item.access_code}
                    component={RouterLink}
                    to={to}
                    onClick={onNavigate}
                    sx={{ ...itemSx(to), pl: 3 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ChevronRightIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={resolveItemLabel(item)}
                      slotProps={{ primary: { sx: { color: 'inherit', fontSize: '0.875rem' } } }}
                    />
                  </ListItemButton>
                );
              })}
            </Box>
          ))}

        {!isPending && sortedSuperGroups.length > 0 && (
          <>
            <ListItemButton
              onClick={() => setSuperOpen((o) => !o)}
              sx={{
                borderRadius: 1,
                mx: 0.5,
                bgcolor: pathIsSuperadmin ? navItemActiveBg : undefined,
                '&.MuiListItemButton-root': { py: 1 },
              }}
            >
              <ListItemText primary="Superadmin" slotProps={{ primary: { sx: { color: 'inherit' } } }} />
              {superOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={superOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {sortedSuperGroups.map((g) => (
                  <Box key={g.key}>
                    {sortedSuperGroups.length > 1 && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', pl: 4, pt: 1, color: 'rgba(255,255,255,0.5)' }}
                      >
                        {formatNavGroupLabel(g.label)}
                      </Typography>
                    )}
                    {g.items.map((item) => {
                      const to = resolveItemHref(item);
                      if (!to) return null;
                      return (
                        <ListItemButton
                          key={item.access_code}
                          component={RouterLink}
                          to={to}
                          onClick={onNavigate}
                          sx={{ ...itemSx(to), pl: 4 }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <ChevronRightIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={resolveItemLabel(item)}
                            slotProps={{ primary: { sx: { color: 'inherit', fontSize: '0.875rem' } } }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </Box>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
      </Box>
    </Box>
  );
}
